import React, { useState, useEffect } from 'react';
import { Guid } from '@Shared.Angular/@types/guid';
import IBusinessDetail from '@Shared.Angular/@types/core/contracts/queryModel/business/businessDetail';
import IExternalUserTaskReport from '@Shared.Angular/@types/core/contracts/queryModel/reports/maintenance/externalUserTaskReport';
import { IFlowStatusReport } from '@Shared.Angular/@types/core/contracts/queryModel/reports/maintenance/flowStatusReport';
import { ReportType } from './reportType';
import { getService } from '@Client/runner.hooks/useService';
import { FlowStatusReport } from './reports/FlowStatusReport';
import { ExternalUserTaskUsageReport } from './reports/ExternalUserTaskUsageReport';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

enum ReportInputType {
  DATE = 'date'
}

interface IReportInput {
  type: ReportInputType;
  id: string;
  name: string;
  value?: string;
}

interface IReport {
  name: string;
  id: ReportType;
  description: string;
  inputs: IReportInput[];
}

type MaintenanceReportsProps = {
  selectedBusiness: IBusinessDetail;
};

export const MaintenanceReports = ({
  selectedBusiness
}: MaintenanceReportsProps) => {
  const maintenanceService = getService('maintenanceService');
  const momentService = getService('momentService');

  const [requestIsPending, setRequestIsPending] = useState(false);
  const [businessId, setBusinessId] = useState<Guid>(null);
  const [flowStatusReportData, setFlowStatusReportData] =
    useState<IFlowStatusReport>(null);
  const [externalUserReportData, setExternalUserReportData] =
    useState<IExternalUserTaskReport[]>(null);

  const getTomorrowDate = () => {
    return momentService.utc().add(1, 'days').format('YYYY-MM-DD');
  };
  const reportTemplates = React.useMemo(() => {
    return [
      {
        name: 'Flow Status (PMI "Template" usage)',
        id: ReportType.FLOW_STATUS,
        description:
          'Get the status of all flows started in the specified UTC timeframe, ordered by Flow Model and starting User',
        inputs: [
          {
            type: ReportInputType.DATE,
            id: 'createdFrom',
            name: 'Flow started date (UTC) >='
          },
          {
            type: ReportInputType.DATE,
            id: 'createdTo',
            name: 'Flow started date (UTC) <',
            value: getTomorrowDate()
          }
        ]
      },
      {
        name: 'External User Task Usage',
        id: ReportType.EXTERNAL_USER_TASK,
        description: 'Report on external user task usage.',
        inputs: [
          {
            type: ReportInputType.DATE,
            id: 'createdFrom',
            name: 'Start date (UTC) >='
          },
          {
            type: ReportInputType.DATE,
            id: 'createdTo',
            name: 'Start date (UTC) <',
            value: getTomorrowDate()
          }
        ]
      }
    ];
  }, []);

  const [selectedReport, setSelectedReport] = useState<IReport>(
    reportTemplates[0]
  );

  useEffect(() => {
    if (selectedBusiness?.id) {
      setBusinessId(selectedBusiness.id);
      setFlowStatusReportData(null);
      setExternalUserReportData(null);
    }
  }, [selectedBusiness]);

  const getReportParams = (report: IReport) => {
    const request: Record<string, string | Guid> = {
      businessId: businessId
    };
    for (const input of report.inputs) {
      if (input.type === ReportInputType.DATE) {
        if (!input.value) {
          getService('notificationService').showErrorToast(
            `Please provide a value for ${input.name}`
          );
          throw new Error(`Missing value for report input ${input.name}`);
        }
        request[input.id + 'Utc'] = momentService
          .utc(input.value, 'YYYY-MM-DD')
          .format('YYYY-MM-DDTHH:mm:ss[Z]');
      } else {
        throw new Error('Unhandled report input type ' + input.type);
      }
    }
    return request;
  };

  const getReport = () => {
    if (!businessId) {
      return;
    }
    setFlowStatusReportData(null);
    setExternalUserReportData(null);
    const params = getReportParams(selectedReport);
    setRequestIsPending(true);
    let request;
    if (selectedReport.id === ReportType.FLOW_STATUS) {
      request = maintenanceService.getFlowStatusReport(params).then((data) => {
        setFlowStatusReportData(data);
        setRequestIsPending(false);
      });
    } else if (selectedReport.id === ReportType.EXTERNAL_USER_TASK) {
      request = maintenanceService
        .getExternalUserTaskReport(params)
        .then((data) => {
          setExternalUserReportData(data);
          setRequestIsPending(false);
        });
    } else {
      setRequestIsPending(false);
      throw new Error('Unhandled report ' + selectedReport.name);
    }
    request.catch(() => {
      setRequestIsPending(false);
    });
  };

  const handleInputChange = (inputId: string, value: string) => {
    setSelectedReport({
      ...selectedReport,
      inputs: selectedReport.inputs.map((input) =>
        input.id === inputId ? { ...input, value } : input
      )
    });
  };

  if (!selectedReport) {
    return null;
  }

  return (
    <div className="row" id="maintenance-reports">
      <div className="col s3">
        <div className="ml-25 mr-30 mt-10 p-0 runner-contents-area">
          <div className="mb-10">
            <label htmlFor="reports">Select a report to run</label>
            <select
              className="browser-default not-clickable"
              id="reports"
              disabled={requestIsPending}
              value={reportTemplates.findIndex(
                (r) => r.id === selectedReport.id
              )}
              onChange={(e) =>
                setSelectedReport(reportTemplates[Number(e.target.value)])
              }
            >
              {reportTemplates.map((report, index) => (
                <option key={report.id} value={index}>
                  {report.name}
                </option>
              ))}
            </select>
            <div id="description">{selectedReport.description}</div>
            <div id="report-inputs">
              {selectedReport.inputs.map((input) => (
                <div key={input.id} className="report-input">
                  <label htmlFor={input.id}>{input.name}</label>
                  {input.type === ReportInputType.DATE && (
                    <input
                      type="date"
                      id={input.id}
                      disabled={requestIsPending}
                      value={input.value || ''}
                      onChange={(e) =>
                        handleInputChange(input.id, e.target.value)
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="mb-10 mt-20 right">
            <TextButton
              type="primary"
              disabled={requestIsPending}
              onClick={getReport}
              text="Run report"
            />
          </div>
        </div>
      </div>
      <div className="col s9">
        {flowStatusReportData &&
          selectedReport.id === ReportType.FLOW_STATUS && (
            <FlowStatusReport data={flowStatusReportData} />
          )}

        {externalUserReportData &&
          selectedReport.id === ReportType.EXTERNAL_USER_TASK && (
            <ExternalUserTaskUsageReport data={externalUserReportData} />
          )}
      </div>
    </div>
  );
};
